// Code -> label lists for the WM Transaction Management Data Profile (Delphi WMD0081, WMTRANSMANAGEMENTDP).
//
// One definition per list, shared by the search panel (`"type": "select"`), the form tabs and the
// listing grid's <Lookup> columns, so a coded column can never render as free text in one place and a
// dropdown in another (rule 3). The codes are the Codes.Strings values on the matching TDBFlagCombo in
// uWMM0081.dfm; the labels are its Items.Strings.

/** The table every layout's fields localize against. */
export const TABLE_NAME = "WMTRANSMANAGEMENTDP";

/** Page title — matches the sidebar, the Home-search entries and the server's EntityName (rule 13). */
export const PAGE_TITLE = "Transaction Management Data Profile";

// Named codes, so no screen types a code letter out by hand (R2). VISIBLE_TABS, DEFAULT_TAB and YES_NO
// mirror the server's TransManagementProfileConstants.VisibleTabs / .DefaultTab and YesNo.
export const STATUS = { ACTIVE: "A", INACTIVE: "I" };

export const YES_NO = { YES: "Y", NO: "N" };

// TABDEFAULTS — which transaction tabs the Transaction Management screen shows.
export const VISIBLE_TABS = {
    RECEIPTS_SHIPMENTS_ALL: "A",
    RECEIPTS_ALL: "B",
    SHIPMENTS_ALL: "C",
    ALL_ONLY: "D",
};

// TRANSTABDEFAULTS — which of them opens first.
export const DEFAULT_TAB = {
    OPEN_SHIPMENTS: "S",
    OPEN_RECEIPTS: "R",
    ALL_TRANSACTIONS: "A",
};

// TRANSTABBASISDATE — which date the Transactions tab's colour thresholds are measured against.
export const TRANS_BASIS_DATE = {
    RCV_SHIP_DATE: "A",
    PLANNED_RCV_SHIP_DATE: "B",
    PLANNED_DEL_PICK_DATE: "C",
    STAGING_START_DATE: "D",
};

// ITEMTABBASISQTY — which quantity the Items tab's colour thresholds are measured against.
export const ITEM_BASIS_QTY = {
    ON_HAND: "A",
    AVAILABLE: "B",
    NET_AVAILABLE: "C",
};

// The colour codes behind all four *COLOR columns.
export const COLOR = {
    NONE: "Z",
    AQUA: "1",
    BLUE: "2",
    FUCHSIA: "3",
    GREEN: "4",
    LIME: "5",
    MAROON: "6",
    NAVY: "7",
    OLIVE: "8",
    PURPLE: "9",
    RED: "R",
    SILVER: "S",
    TEAL: "T",
    WHITE: "W",
    YELLOW: "Y",
};

export const STATUS_OPTIONS = [
    { id: STATUS.ACTIVE, text: "Active" },
    { id: STATUS.INACTIVE, text: "Inactive" },
];

export const YES_NO_OPTIONS = [
    { id: YES_NO.YES, text: "Yes" },
    { id: YES_NO.NO, text: "No" },
];

export const VISIBLE_TABS_OPTIONS = [
    { id: VISIBLE_TABS.RECEIPTS_SHIPMENTS_ALL, text: "Open Receipts / Open Shipments / All Transactions" },
    { id: VISIBLE_TABS.RECEIPTS_ALL, text: "Open Receipts / All Transactions" },
    { id: VISIBLE_TABS.SHIPMENTS_ALL, text: "Open Shipments / All Transactions" },
    { id: VISIBLE_TABS.ALL_ONLY, text: "All Transactions" },
];

// The full Default Tab list; what a given record may actually hold is narrowed by its TABDEFAULTS
// value, see ALLOWED_DEFAULT_TABS.
export const DEFAULT_TAB_OPTIONS = [
    { id: DEFAULT_TAB.OPEN_SHIPMENTS, text: "Open Shipments" },
    { id: DEFAULT_TAB.OPEN_RECEIPTS, text: "Open Receipts" },
    { id: DEFAULT_TAB.ALL_TRANSACTIONS, text: "All Transactions" },
];

// uWMD0081.PAS SetDefaultTab: the Delphi screen added and removed items from the Default Tab combo as
// Visible Tabs changed — 'D' forced All Transactions and disabled the control, 'B' dropped Open
// Shipments, 'C' dropped Open Receipts, 'A' kept all three. Mirrored here so the dropdown offers only
// what the user may pick; the same table is enforced server-side
// (TransManagementProfileConstants.AllowedDefaultTabs) so a direct API call cannot bypass it.
export const ALLOWED_DEFAULT_TABS = {
    [VISIBLE_TABS.RECEIPTS_SHIPMENTS_ALL]: [DEFAULT_TAB.OPEN_SHIPMENTS, DEFAULT_TAB.OPEN_RECEIPTS, DEFAULT_TAB.ALL_TRANSACTIONS],
    [VISIBLE_TABS.RECEIPTS_ALL]: [DEFAULT_TAB.OPEN_RECEIPTS, DEFAULT_TAB.ALL_TRANSACTIONS],
    [VISIBLE_TABS.SHIPMENTS_ALL]: [DEFAULT_TAB.OPEN_SHIPMENTS, DEFAULT_TAB.ALL_TRANSACTIONS],
    [VISIBLE_TABS.ALL_ONLY]: [DEFAULT_TAB.ALL_TRANSACTIONS],
};

/**
 * The Default Tab options legal for a Visible Tabs value. An unrecognised or blank Visible Tabs puts
 * no restriction on the list, matching the Delphi case statement's fall-through.
 */
export const defaultTabOptionsFor = (visibleTabs) => {
    const allowed = ALLOWED_DEFAULT_TABS[visibleTabs];
    return allowed ? DEFAULT_TAB_OPTIONS.filter((o) => allowed.includes(o.id)) : DEFAULT_TAB_OPTIONS;
};

export const TRANS_BASIS_DATE_OPTIONS = [
    { id: TRANS_BASIS_DATE.RCV_SHIP_DATE, text: "Rcv / Ship Date" },
    { id: TRANS_BASIS_DATE.PLANNED_RCV_SHIP_DATE, text: "Planned Rcv / Shp Date" },
    { id: TRANS_BASIS_DATE.PLANNED_DEL_PICK_DATE, text: "Planned Del / Pick Date" },
    { id: TRANS_BASIS_DATE.STAGING_START_DATE, text: "Staging Start Date" },
];

export const ITEM_BASIS_QTY_OPTIONS = [
    { id: ITEM_BASIS_QTY.ON_HAND, text: "On Hand" },
    { id: ITEM_BASIS_QTY.AVAILABLE, text: "Available" },
    { id: ITEM_BASIS_QTY.NET_AVAILABLE, text: "Net Available" },
];

// uWMM0081.dfm spells code '4' as 'Greesn' for ITEMTABWARNINGCOLOR only; all three other colour
// dropdowns spell it 'Green'. The typo is corrected here (approved decision 3) — it is a display label
// and the stored code '4' is unaffected either way — so one list serves all four columns instead of
// one list plus a near-identical copy.
export const COLOR_OPTIONS = [
    { id: COLOR.NONE, text: "None" },
    { id: COLOR.AQUA, text: "Aqua" },
    { id: COLOR.BLUE, text: "Blue" },
    { id: COLOR.FUCHSIA, text: "Fuchsia" },
    { id: COLOR.GREEN, text: "Green" },
    { id: COLOR.LIME, text: "Lime" },
    { id: COLOR.MAROON, text: "Maroon" },
    { id: COLOR.NAVY, text: "Navy" },
    { id: COLOR.OLIVE, text: "Olive" },
    { id: COLOR.PURPLE, text: "Purple" },
    { id: COLOR.RED, text: "Red" },
    { id: COLOR.SILVER, text: "Silver" },
    { id: COLOR.TEAL, text: "Teal" },
    { id: COLOR.WHITE, text: "White" },
    { id: COLOR.YELLOW, text: "Yellow" },
];

/**
 * Which list each coded column carries, keyed by the DTO property name. One map for the search panel,
 * the form tabs and the grid, so a column cannot end up with one list in one place and another
 * somewhere else.
 *
 * TransTabDefaults is present with its full list; the form narrows it per record through
 * defaultTabOptionsFor, and the search panel deliberately offers all three so a user can find the
 * records that hold any of them.
 */
export const FIELD_OPTIONS = {
    Status: STATUS_OPTIONS,
    TabDefaults: VISIBLE_TABS_OPTIONS,
    TransTabDefaults: DEFAULT_TAB_OPTIONS,
    EdiOrdersTab: YES_NO_OPTIONS,
    WebOrdersTab: YES_NO_OPTIONS,
    CustomersTab: YES_NO_OPTIONS,
    ItemTab: YES_NO_OPTIONS,
    LotTab: YES_NO_OPTIONS,
    SubLotTab: YES_NO_OPTIONS,
    LocationTab: YES_NO_OPTIONS,
    UninvoicedChargesTab: YES_NO_OPTIONS,
    TransTabUseColors: YES_NO_OPTIONS,
    TransTabBasisDate: TRANS_BASIS_DATE_OPTIONS,
    TransTabPendingColor: COLOR_OPTIONS,
    TransTabWarningColor: COLOR_OPTIONS,
    ItemTabUseColors: YES_NO_OPTIONS,
    ItemTabBasisQty: ITEM_BASIS_QTY_OPTIONS,
    ItemTabPendingColor: COLOR_OPTIONS,
    ItemTabWarningColor: COLOR_OPTIONS,
};
